export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
/** Alert used to reject in-flight (non-offline-safe) requests on socket drop. */
export function disconnectedAlert() {
    return { code: 'INTERNAL', message: 'errors:ws.disconnected', fields: [] };
}
/** Alert used to reject in-flight requests when the client is torn down. */
export function clientClosedAlert() {
    return { code: 'INTERNAL', message: 'errors:ws.client_closed', fields: [] };
}
/** Alert used when a request receives no `res`/`err` within its timeout. */
export function requestTimeoutAlert(op) {
    return { code: 'INTERNAL', message: 'errors:ws.request_timeout', fields: [{ path: 'op', message: op }] };
}
export class CorrelationRegistry {
    pending = new Map();
    /**
     * Registers a waiter for `id`. Idempotent: calling it again for an `id`
     * already pending returns the SAME promise instead of creating a second
     * waiter — this is what makes offline-queue flush retries safe (§5.10-4).
     */
    register(id, op, opts = {}) {
        const existing = this.pending.get(id);
        if (existing)
            return existing.promise;
        let resolveFn;
        let rejectFn;
        const promise = new Promise((resolve, reject) => {
            resolveFn = resolve;
            rejectFn = reject;
        });
        const timeoutMs = opts.timeoutMs === undefined ? DEFAULT_REQUEST_TIMEOUT_MS : opts.timeoutMs;
        const timer = timeoutMs === null
            ? null
            : setTimeout(() => {
                this.pending.delete(id);
                rejectFn(requestTimeoutAlert(op));
            }, timeoutMs);
        this.pending.set(id, {
            promise,
            resolve: resolveFn,
            reject: rejectFn,
            timer,
            op,
            offlineSafe: opts.offlineSafe ?? false,
        });
        return promise;
    }
    /** Resolves a pending `id` with a `res` payload. Returns false if unknown. */
    resolve(id, payload) {
        const entry = this.pending.get(id);
        if (!entry)
            return false;
        if (entry.timer)
            clearTimeout(entry.timer);
        this.pending.delete(id);
        entry.resolve(payload);
        return true;
    }
    /** Rejects a pending `id` with an `err` alert. Returns false if unknown. */
    reject(id, alert) {
        const entry = this.pending.get(id);
        if (!entry)
            return false;
        if (entry.timer)
            clearTimeout(entry.timer);
        this.pending.delete(id);
        entry.reject(alert);
        return true;
    }
    /** True if `id` is still awaiting a reply (used to dedupe retries). */
    has(id) {
        return this.pending.has(id);
    }
    /** The live promise for `id`, if any is pending. */
    peek(id) {
        return this.pending.get(id)?.promise;
    }
    size() {
        return this.pending.size;
    }
    /**
     * Rejects every pending entry with `alert`. By default skips
     * `offlineSafe` entries (a drop must not fail a queued optimistic
     * action) — pass `includeOfflineSafe: true` on a full teardown
     * (`disconnect()`), where nothing should be left dangling.
     */
    rejectAll(alert, opts = {}) {
        const includeOfflineSafe = opts.includeOfflineSafe ?? false;
        for (const [id, entry] of Array.from(this.pending.entries())) {
            if (entry.offlineSafe && !includeOfflineSafe)
                continue;
            if (entry.timer)
                clearTimeout(entry.timer);
            this.pending.delete(id);
            entry.reject(alert);
        }
    }
}
